/**
 * A wrapper for eu-data-act related endpoints.
 */
import type {
  AcceptTerms,
  CancelTerms,
  EuDataActDataSharingDto,
  EuDataActRawDataDto,
  EuDataActTransferDto,
} from "@strtmn/sds/types"
import { Cache } from "aws-amplify/utils"
import { isUserSignedIn } from "../Utils.AmplifyAuth"
import { clearCacheMachineData } from "./MachinesApi"
import {
  getCacheKey,
  getFromApi,
  postToApi,
  putToApi,
  type ApiResponse,
} from "./utils"
import { clearCacheCurrentUserData } from "./userData"

// 60 minutes
const EXPIRES_IN_MS = 60 * 60 * 1000

export const getCurrentTermsVersion = async (): Promise<string | false> => {
  console.debug("> getCurrentTermsVersion")
  const apiName = "frontendApi"
  const path = "/eu-data-act/current-terms-version"
  const cacheKey = getCacheKey(apiName, path)

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    // console.log('  try to get item from cache')
    // console.warn('  remove item from cache') // DBG
    // await Cache.removeItem(cacheKey) // DBG
    let data: string | null = await Cache.getItem(cacheKey)
    if (data) {
      console.debug(
        "getCurrentTermsVersion: Found in cache:",
        data,
        `${cacheKey}`
      )
    } else {
      console.log(cacheKey, "not found in cache. Loading from API...")
      data = (await getFromApi(apiName, path)).json as string
      await Cache.setItem(cacheKey, data, {
        expires: new Date().getTime() + EXPIRES_IN_MS,
      })
      console.log("getCurrentTermsVersion: Fetched from api:", data)
    }

    // console.log("✅ getCurrentTermsVersion: data")
    return data
  } catch (err) {
    console.error(
      "Error fetching Terms data from cache (unlikely) or api (likely)."
    )
    console.info("Error:", err)
    throw err
  }
}

export const acceptTerms = async (
  machineSerialNumber: string,
  termsVersion: string,
  shareWithDealer: boolean
): Promise<string | false> => {
  console.debug("> acceptTerms")
  if (!(await isUserSignedIn())) {
    return false
  }

  const data: AcceptTerms = {
    machineSerialNumber,
    termsVersion,
    shareWithDealer,
  }
  const { json, response } = await putToApi(
    "frontendApi",
    "/eu-data-act/accept-terms",
    data
  )
  console.log("Got API response:", response)
  await clearCacheMachineData(
    machineSerialNumber
  ) /** @todo: clearCacheMachineData expects a machine_id! */
  await clearCacheCurrentUserData()
  return json
}

export const cancelTerms = async (
  machineSerialNumber: string
): Promise<string | false> => {
  console.debug("> cancelTerms")
  if (!(await isUserSignedIn())) {
    return false
  }

  const data: CancelTerms = {
    machineSerialNumber,
  }
  const { json, response } = await putToApi(
    "frontendApi",
    "/eu-data-act/cancel-terms",
    data
  )
  console.log("Got API response:", response)
  await clearCacheMachineData(
    machineSerialNumber
  ) /** @todo: clearCacheMachineData expects a machine_id! */
  await clearCacheCurrentUserData()
  return json
}

export const requestDataSharing = async (
  data: EuDataActDataSharingDto
): Promise<ApiResponse<any>> => {
  console.debug("> requestDataSharing")
  const currentUser = await isUserSignedIn()
  if (!currentUser) {
    return {
      success: false,
      error: "User not signed in",
    }
  }

  data.userId = currentUser.userId
  const { json, response } = await postToApi(
    "frontendApi",
    "/eu-data-act/request-data-sharing",
    data
  )
  console.log("Got API response:", response)
  return {
    success: true,
    data: json,
    message: "Form submitted successfully",
  }
}

export const requestTransfer = async (
  data: EuDataActTransferDto
): Promise<ApiResponse<any>> => {
  console.debug("> requestTransfer")
  const currentUser = await isUserSignedIn()
  if (!currentUser) {
    return {
      success: false,
      error: "User not signed in",
    }
  }

  data.userId = currentUser.userId
  const { json, response } = await postToApi(
    "frontendApi",
    "/eu-data-act/request-transfer",
    data
  )
  console.log("Got API response:", response)
  return {
    success: true,
    data: json,
    message: "Form submitted successfully",
  }
}

export const requestRawData = async (
  data: EuDataActRawDataDto
): Promise<ApiResponse<any>> => {
  console.debug("> requestRawData")
  const currentUser = await isUserSignedIn()
  if (!currentUser) {
    return {
      success: false,
      error: "User not signed in",
    }
  }

  data.userId = currentUser.userId
  const { json, response } = await postToApi(
    "frontendApi",
    "/eu-data-act/request-raw-data",
    data
  )
  console.log("Got API response:", response)
  return {
    success: true,
    data: json,
    message: "Form submitted successfully",
  }
}
